import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  IconHeadset,
  IconPlus,
  IconChevronRight,
  IconMessage2,
  IconInbox,
} from "@tabler/icons-react";
import { listTickets } from "../api";
import { STATUS_TONE, formatDateTime } from "../constants";
import { cn } from "@/lib/utils/cn";
import { NewTicketModal } from "../components/NewTicketModal";

export function SupportPage() {
  const { t } = useTranslation();
  const [isNewOpen, setIsNewOpen] = useState(false);

  const { data: tickets, isLoading } = useQuery({
    queryKey: ["agency-support-tickets"],
    queryFn: listTickets,
  });

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-col gap-1">
          <h2 className="flex items-center gap-2 text-3xl font-black tracking-tight text-ink">
            <IconHeadset size={28} className="text-brand-600" />
            {t("support.title")}
          </h2>
          <p className="text-ink-soft font-medium">{t("support.subtitle")}</p>
        </div>

        <button
          onClick={() => setIsNewOpen(true)}
          className="inline-flex items-center justify-center gap-2 rounded-2xl bg-brand-grad px-6 py-3 text-sm font-black uppercase tracking-wider text-white shadow-glow transition-all hover:scale-105 active:scale-95"
        >
          <IconPlus size={18} />
          {t("support.newTicket")}
        </button>
      </div>

      <div className="rounded-3xl border border-white/40 bg-white/70 shadow-card backdrop-blur-sm overflow-hidden">
        <div className="border-b border-border-soft bg-gray-50/30 px-8 py-5">
          <h3 className="font-black text-ink">{t("support.myTickets")}</h3>
        </div>

        {isLoading ? (
          <div className="p-10 text-center text-ink-soft font-bold italic animate-pulse">
            {t("support.loadingList")}
          </div>
        ) : !tickets || tickets.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 p-16 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-brand-50 text-brand-300">
              <IconInbox size={32} />
            </div>
            <h4 className="text-lg font-bold text-ink">
              {t("support.emptyTitle")}
            </h4>
            <p className="max-w-xs text-sm text-ink-muted">
              {t("support.emptyDesc")}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-border-soft">
            {tickets.map((ticket) => (
              <li key={ticket.id}>
                <Link
                  to={`/support/${ticket.id}`}
                  className="group flex items-center gap-4 px-8 py-5 transition-colors hover:bg-white/50"
                >
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-brand-50 text-brand-600 shadow-inner-light">
                    <IconMessage2 size={20} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-black uppercase tracking-wider text-ink-soft">
                        {ticket.code}
                      </span>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-brand-400">
                        {t(`support.category.${ticket.category}`)}
                      </span>
                    </div>
                    <p className="truncate font-bold text-ink">
                      {ticket.subject}
                    </p>
                    <p className="text-xs text-ink-muted">
                      {t("support.updatedAt", {
                        date: formatDateTime(ticket.updatedAt),
                      })}
                    </p>
                  </div>
                  <span
                    className={cn(
                      "inline-flex shrink-0 items-center rounded-full px-3 py-1 text-[11px] font-black uppercase tracking-wider",
                      STATUS_TONE[ticket.status],
                    )}
                  >
                    {t(`support.status.${ticket.status}`)}
                  </span>
                  <IconChevronRight
                    size={18}
                    className="shrink-0 text-gray-300 transition-transform group-hover:translate-x-1 group-hover:text-brand-600"
                  />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>

      <NewTicketModal open={isNewOpen} onOpenChange={setIsNewOpen} />
    </div>
  );
}
